// ============================================================
// TERROR — DUNGEON 1 (v2 — full rebuild)
// Outskirts of the Minerium
// The Last Flame of the Minerium — T'yrent's domain
//
// DESIGN LANGUAGE: Pokémon FireRed — Indigo Plateau / Power
// Plant / Rocket Hideout. Separate floors with stair
// transitions and title cards. Mazes with dead ends that pay
// out. Hidden items on plain tiles. Warp glyphs. A signature
// floor mechanic per ilk. Bosses standing in the open.
//
// ── FLOOR PLAN ──────────────────────────────────────────────
//
//  B1F — THE OUTER VEIL        (Power Plant maze)
//        4-flame order puzzle scattered through the maze,
//        mural hint, chest + hidden item in dead ends,
//        locked door guarding the stairs down.
//
//  B2F — THE DRIFT             (Space-ilk signature mechanic)
//        Zero-gravity void slide chamber (ice-gym physics —
//        you drift until you hit something). Warp glyphs.
//        Lever reachable only by the correct slide route.
//        SECRET: ride the spawn warp 3 times in a row.
//        Verified solvable: 5 slides to the lever,
//        6 to the hidden page, 7 from lever to the door.
//
//  B3F — THE WATCHING DARK     (dark cave + trainer corridors)
//        Limited vision. Light 4 braziers to see and to learn
//        the seal order. Step 4 seals in order. S'uhnker and
//        Z'hefir physically block corridors like trainers.
//
//  SANCTUM — THE LAST FLAME
//        T'yrent stands silently in the open (Zapdos energy).
//        Oath Tablet easter egg (appears after the B1F mural
//        is read twice). Post-boss chest. Hidden page.
//
// ── ILK MECHANIC SYSTEM ─────────────────────────────────────
// Every ilk's dungeons get a SIGNATURE floor mechanic:
//   SPACE   → void drift (this dungeon, B2F)
//   TIME    → (future) rewind tiles / clockwork rotation floors
//   PHANTOM → (future) phase walls / mirrored twin floors
//   DRAGON  → (future) ember tides / collapsing floors
// Same skeleton, swap THEME + the B2F-slot mechanic.
//
// ── PROPS (unchanged interface) ─────────────────────────────
//   profile      — player profile (chocolateCoins, gender, story)
//   onExit       — () → back to Plaza
//   onBossBattle — (opponentId) → Terror card battle
//   Boss results read from profile.storyProgress.defeatedOpponents
//
// ── ART ASSETS (CSS scaffold now, swap to CDN webp later) ───
// CDN: https://terror-assets.pages.dev/dungeon/
// floor.webp          — dark cosmic basalt, seamless
// wall.webp           — rune-carved wall, purple crack glow
// slide_tile.webp     — void shimmer, concentric purple rings
// warp_glyph.webp     — floor glyph, slow rotating star ring
// stairs_down.webp    — descending stone stairs into dark
// stairs_up.webp      — ascending stairs, faint light above
// door_locked.webp    — iron door, purple seal energy
// door_open.webp      — same door ajar, glow beyond
// flame_unlit.webp    — cold brazier bowl on pedestal
// flame_lit_*.webp    — per-ilk flame (phantom/time/dragon/space)
// flame_corrupt.webp  — sickly violet wrongflame
// brazier.webp        — tall iron brazier, unlit / lit variants
// seal_tile.webp      — pressure plate / seal_active.webp lit
// lever.webp          — iron lever off / lever_pulled.webp on
// chest_closed.webp / chest_open.webp
// mural_oath.webp     — wall mural, 4 ilk symbols in sequence
// tablet_oath.webp    — stone tablet, 4 ilk symbols in cross
// grimoire_page.webp  — torn glowing parchment
// eyes.webp           — two green eyes in darkness (J'ynxi)
// boss_suhnker.webp / boss_zhefir.webp / boss_tyrent.webp
// ============================================================

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { SD } from "./theme";
import {
  CDN,
  PlayerEntity,
  MobileDpad,
  DialogueToast,
  ProximityBubble,
} from "./engineIndex";
import { useEquippedCompanion } from "./useEquippedCompanion";
import { ChocolateCoin } from "./CoinGlyph";
import { recordDungeonSecret, DUNGEON_SECRET_IDS } from "./progression";
import { auth } from "./firebase";

// ─────────────────────────────────────────────────────────────
// ILKS
// ─────────────────────────────────────────────────────────────
const ILKS = {
  SPACE:   { id: "space",   color: "#6d28d9", glow: "#7c3aed", label: "Space",   sym: "✦" },
  TIME:    { id: "time",    color: "#22d3ee", glow: "#06b6d4", label: "Time",    sym: "⟳" },
  PHANTOM: { id: "phantom", color: "#c9a84c", glow: "#d4a84b", label: "Phantom", sym: "◇" },
  DRAGON:  { id: "dragon",  color: "#dc2626", glow: "#ef4444", label: "Dragon",  sym: "◈" },
};

// ─────────────────────────────────────────────────────────────
// DUNGEON THEME MODULE — one of these per ilk region.
// Dungeon 1 lives in the Space Emperium; T'yrent's Dragon
// aura bleeds into the Sanctum.
// ─────────────────────────────────────────────────────────────
const SPACE_THEME = {
  id: "space",
  name: "Space Emperium",
  stone: {
    deepest:   "#04020a",
    base:      "#0a0418",
    mid:       "#14091e",
    highlight: "#1d1130",
    wall:      "#06020f",
    wallLit:   "#10062a",
  },
  mortar:    "#2d1060",
  mortarHot: "#6d28d9",
  torch:     "#c8963c",
  torchPool: "rgba(255,140,0,0.16)",
  corrupt:   "#9333ea",
  bossAura:  "#dc262622",
  borderCool:"#2a1050",
  borderHot: "#7c3aed44",
  runeInk:   "#4c1d95",
};
const THEME = SPACE_THEME;

// ─────────────────────────────────────────────────────────────
// FLOOR MAPS — ASCII source of truth.
// Verified by pathfinding before shipping: every flame, chest,
// hidden item, seal, brazier, lever and stairwell is reachable;
// stairs are reachable ONLY through their door.
//
// LEGEND
//   #  wall            .  floor
//   ~  void drift tile (slide until you hit something)
//   E  stairs up / exit          S  stairs down
//   D  locked door (opens when the floor is solved)
//   W  warp glyph                L  lever
//   a-d flame pedestals          M  mural
//   B  brazier                   s  seal plate
//   C  chest                     *  hidden item (renders as floor)
//   T  T'yrent                   O  Oath Tablet
// ─────────────────────────────────────────────────────────────
const MAPS = {
  B1F: [
    "#######################",
    "#E..#...#...........#S#",
    "###.#.#.#.###.#####.#D#",
    "#...#.#.#.#.#...#C..#.#",
    "#.###.#.#.#.###.###.#.#",
    "#.#.#.....#..c#...#...#",
    "#.#.#.#####.#####.###.#",
    "#.#...#.....#...#...#a#",
    "#.#.###.#####.#.#...###",
    "#.#.....#..M..#.#b#...#",
    "#.#####.#.#####.#####.#",
    "#...#...#.#...#.....#.#",
    "###.#.###.#.#.#####.#.#",
    "#...#.....#.#*#.......#",
    "#.#######.#.###.#####.#",
    "#.#.............#...#.#",
    "#.#.#####.#..####.#.#.#",
    "#...#d............#...#",
    "#######################",
  ],
  B2F: [
    "########################",
    "#E.W####################",
    "######W.~~~~~~#~~~~~~~##",
    "######~~~~~~~~~~~~~~~~##",
    "######~~~~~~~~#~~##~~~##",
    "######~~~~~~~~~~~~~~~###",
    "######~~~~~~~#~~~~~~~~##",
    "######~~~~~~~#~~~~~~~~##",
    "######~~~~~~~~~~~~~#.L##",
    "######~~~~~~#~~~~#~~~~##",
    "######~~~~~#~~~~~~~~~~##",
    "######~~~~~~#~~~~~~~~~##",
    "######~~#~~~~~~~~~#~~*##",
    "#############D##########",
    "##CW.########.........##",
    "###################.####",
    "##################....S#",
    "########################",
  ],
  B3F: [
    "#######################",
    "#E......#s....#.......#",
    "#######.###.#.#...###.#",
    "#s........#B#...#...#.#",
    "##..#.###.###.#.###.#.#",
    "#...#..B#.....#...#.#.#",
    "#.###########.###.#.#.#",
    "#.....#.......#...#...#",
    "#.###.#.#######.###.###",
    "#..s#.#......B#...#...#",
    "#####.#.###.#####.###.#",
    "#.....#...#.#.....#...#",
    "#.###.###.#.#.##.##.#.#",
    "#...#..s#.#.#.#...#.#.#",
    "###...###.#.#.#.#.#.#.#",
    "#...#B#...#...#.#...#.#",
    "#.#####.#######.#####.#",
    "#...............#SD...#",
    "#######################",
  ],
  SANCTUM: [
    "################",
    "#......T.......#",
    "#..............#",
    "#....######....#",
    "#....#....#....#",
    "#.C..#.O..#..*.#",
    "#....#....#....#",
    "#....##..##....#",
    "#......E.......#",
    "################",
  ],
};

// ─────────────────────────────────────────────────────────────
// FLOOR META — names, atmosphere, mechanics wiring
// ─────────────────────────────────────────────────────────────
const FLOOR_ORDER = ["B1F", "B2F", "B3F", "SANCTUM"];

const FLOOR_META = {
  B1F: {
    label: "B1F",
    title: "The Outer Veil",
    ambient: "Four flames sleep in the maze. The order matters.",
    tint: "rgba(120,80,200,0.04)",
    dark: false,
    doorHint: "Sealed by oath. Four flames, one order. The mural remembers.",
  },
  B2F: {
    label: "B2F",
    title: "The Drift",
    ambient: "Gravity forgot this floor. You will not stop until something stops you.",
    tint: "rgba(80,40,120,0.06)",
    dark: false,
    doorHint: "Sealed. Somewhere across the drift, a lever waits.",
  },
  B3F: {
    label: "B3F",
    title: "The Watching Dark",
    ambient: null, // silence
    tint: null,
    dark: true,
    visRadius: 2.6,
    doorHint: "Sealed. The dark counts your steps. Light it, learn it, walk it.",
  },
  SANCTUM: {
    label: "SANCTUM",
    title: "The Last Flame",
    ambient: "He has been standing here longer than the stones.",
    tint: "rgba(180,40,40,0.06)",
    dark: false,
    doorHint: null,
  },
};

// Flame puzzle (B1F) — light in this ilk order
const FLAME_DEFS = {
  a: { ilk: ILKS.PHANTOM, order: 1 },
  b: { ilk: ILKS.TIME,    order: 2 },
  c: { ilk: ILKS.DRAGON,  order: 3 },
  d: { ilk: ILKS.SPACE,   order: 4 },
};

// Warp pairs (B2F). Keys are "x,y" of the glyph.
// secret: ride the SAME glyph 3 times in a row → secret pocket.
const WARPS = {
  "3,1":  { dest: { x: 7, y: 2 },  color: "#7c3aed",
            secretAfter: 3, secretDest: { x: 3, y: 14 } },
  "6,2":  { dest: { x: 2, y: 1 },  color: "#22d3ee" },
  "3,14": { dest: { x: 2, y: 1 },  color: "#c9a84c" },
};

// Seal order (B3F) — revealed by braziers. Keyed "x,y".
const SEAL_ORDER = ["9,1", "1,3", "7,13", "3,9"]; // I → II → III → IV
const SEAL_ROMAN = { "9,1": "I", "1,3": "II", "7,13": "III", "3,9": "IV" };

// Brazier → hint pairing (each lit brazier names one seal)
const BRAZIER_HINTS = {
  "11,3": "A seal near the north passage. The flame calls it FIRST.",
  "7,5":  "A seal by the west wall. The flame calls it SECOND.",
  "13,9": "A seal deep in the south maze. The flame calls it THIRD.",
  "5,15": "A seal in the low corridor. The flame calls it LAST.",
};

// Bosses — positions + gating
const BOSSES = {
  suhnker: { floor: "B3F", x: 12, y: 5,  name: "S'uhnker",
             color: "#7c3aed", needs: [] },
  zhefir:  { floor: "B3F", x: 19, y: 9,  name: "Z'hefir",
             color: "#c9a84c", needs: ["suhnker"] },
  tyrent:  { floor: "SANCTUM", x: 7, y: 1, name: "T'yrent",
             color: "#dc2626", needs: ["suhnker", "zhefir"] },
};

// Hidden items + chests.
// D1 COMPLETION WAVE: rewards are now REAL. Every collect fires
// `recordDungeonSecret` (progression.js) — an atomic, idempotent
// Firebase stamp at storyProgress/dungeonSecrets/d1/{id} that
// grants coins (delta transaction, IE Currency Lane compliant via
// awardCrests) and/or cards exactly once, ever. Local `secretsFound`
// still drives same-session UI; the Firebase stamps are the truth
// that survives localStorage wipes and device changes.
const TREASURES = {
  "B1F:13,13":    { kind: "hidden", id: "veil_cache",
                    title: "Coin Cache", crests: 50,
                    lines: ["Loose stone. Under it — coins, stashed by someone who never came back for them.", "◕ 50 Chocolate Coins found."] },
  "B1F:17,3":     { kind: "chest", id: "veil_chest",
                    title: "Dusty Chest", crests: 25,
                    lines: ["The lid resists, then gives.", "Crests, and a smell like old lightning.", "◆ 25 Divine Crests found."] },
  "B2F:21,12":    { kind: "hidden", id: "grimoire_page_1",
                    title: "Grimoire Page", crests: 0,
                    lines: ["A page, pinned to the rock by drift-pressure.", '"...the seventh seal was never meant to hold..."', "◆ Grimoire Page collected. (1 of ???)"] },
  "B2F:2,14":     { kind: "chest", id: "drift_secret_chest",
                    title: "Sealed Coffer",  crests: 0, secretVariant: true,
                    lines: ["No door leads here. You arrived anyway.", "Z'hefir's voice, if she had spoken:", '"Your shadow found this. Not you."', "◆ S'uhnker Story Variant — earned."] },
  "SANCTUM:13,5": { kind: "hidden", id: "grimoire_page_2",
                    title: "Grimoire Page", crests: 0,
                    lines: ["Another page. Burned at the edges by something patient.", '"...J\u2019ynxi knew what X\u2019aol would do..."', "◆ Grimoire Page collected. (2 of ???)"] },
  "SANCTUM:2,5":  { kind: "chest", id: "sanctum_chest", needsBoss: "tyrent",
                    title: "The Warden's Chest", crests: 100,
                    lines: ["It only opens for whoever the flame no longer fears.", "◆ 100 Divine Crests found."] },
};

// This dungeon's id in DUNGEONS / storyProgress.
const DUNGEON_ID = 1;

// What each secret ACTUALLY grants when stamped (first find only).
// Card variants use existing bespoke holo tiers:
//   drift chest  → S'uhnker's mascot (H-09 Lord) in VOID foil — the
//                  Maelstrom, native to the Space Emperium's drift.
//   oath tablet  → Chaos Dragon Lord in SOLREND foil — T'yrent's
//                  sun-rend crimson. (A true bespoke "easter egg"
//                  tier is a future HoloFoil session; swapping the
//                  tier string here is the only change needed.)
const SECRET_REWARDS = {
  veil_cache:         { crests: 50 },
  veil_chest:         { crests: 25 },
  grimoire_page_1:    {},
  drift_secret_chest: { card: { id: "h09_lord", holoTier: "holo_void" } },
  grimoire_page_2:    {},
  sanctum_chest:      { crests: 100 },
  easter_egg_tablet:  { card: { id: "chaos_dragon_lord", holoTier: "holo_solrend" } },
};

// Canonical countable secrets for the HUD "✶ X/Y" counter.
const SECRET_IDS = DUNGEON_SECRET_IDS[DUNGEON_ID] || [];

// ─────────────────────────────────────────────────────────────
// MAP PARSER — grid + entity extraction. Pads short rows and
// console.warns on width mismatches so a stray typo never
// crashes the floor.
// ─────────────────────────────────────────────────────────────
function parseFloor(floorId) {
  const src = MAPS[floorId];
  const cols = Math.max(...src.map(r => r.length));
  const rows = src.length;
  const grid = [];
  const ent = {
    flames: [], warps: [], chests: [], hidden: [],
    braziers: [], seals: [], mural: null, lever: null,
    door: null, stairsDown: null, stairsUp: null, tablet: null,
  };
  for (let y = 0; y < rows; y++) {
    let line = src[y];
    if (line.length !== cols) {
      console.warn(`[Dungeon1] ${floorId} row ${y} width ${line.length} != ${cols} — padding`);
      line = line.padEnd(cols, "#");
    }
    const row = [];
    for (let x = 0; x < cols; x++) {
      const c = line[x];
      let cell = "."; // default floor under entities
      if (c === "#") cell = "#";
      else if (c === "~") cell = "~";
      else if (c === "D") { cell = "D"; ent.door = { x, y }; }
      else if (c === "S") { cell = "S"; ent.stairsDown = { x, y }; }
      else if (c === "E") { cell = "E"; ent.stairsUp = { x, y }; }
      else if ("abcd".includes(c)) { cell = "F"; ent.flames.push({ id: c, x, y, ...FLAME_DEFS[c] }); }
      else if (c === "W") { cell = "W"; ent.warps.push({ x, y, ...(WARPS[`${x},${y}`] || {}) }); }
      else if (c === "C") { cell = "C"; ent.chests.push({ x, y }); }
      else if (c === "*") { cell = "*"; ent.hidden.push({ x, y }); }
      else if (c === "B") { cell = "B"; ent.braziers.push({ x, y }); }
      else if (c === "s") { cell = "s"; ent.seals.push({ x, y }); }
      else if (c === "M") { cell = "M"; ent.mural = { x, y }; }
      else if (c === "L") { cell = "L"; ent.lever = { x, y }; }
      else if (c === "T") { cell = "T"; }
      else if (c === "O") { cell = "O"; ent.tablet = { x, y }; }
      row.push(cell);
    }
    grid.push(row);
  }
  return { id: floorId, cols, rows, grid, ent };
}

const FLOORS = {};
FLOOR_ORDER.forEach(f => { FLOORS[f] = parseFloor(f); });

// ─────────────────────────────────────────────────────────────
// VIEWPORT / CAMERA
// ─────────────────────────────────────────────────────────────
// ⚠ THE POINTER LAW (2026-08-22) — TOUCH ALONE IS NOT "MOBILE".
// `navigator.maxTouchPoints > 0` is true on every touchscreen laptop and
// 2-in-1, so this said "phone" on caP'n's PC. In OrientationLock it was a
// hard lockout (the rotate curtain over a monitor that cannot rotate);
// here it is thumb controls and phone layout handed to someone with a
// mouse. `(pointer: coarse)` asks about the PRIMARY pointer instead of
// what the hardware merely supports: phone/tablet coarse, touchscreen PC
// fine. Touch is still required, so mouse-only desktops are unchanged.
// Full reasoning lives once, on isMobile() in OrientationLock.js.
const isMobile = () => {
  if (typeof window === "undefined") return false;
  if (!(("ontouchstart" in window) || (navigator.maxTouchPoints || 0) > 0)) return false;
  try {
    if (typeof window.matchMedia === "function") return window.matchMedia("(pointer: coarse)").matches;
  } catch { /* fall back to the touch-only answer rather than throw */ }
  return true;
};

function calcTile(cols) {
  if (isMobile() && window.innerHeight > window.innerWidth) {
    return Math.max(Math.floor(window.innerWidth / Math.min(cols, 16)), 24);
  }
  // COVER the viewport: take the larger ratio so the map always
  // fills the screen (camera scrolls the rest). Capped for sanity.
  const t = Math.max(window.innerWidth / cols, window.innerHeight / 14);
  return Math.min(Math.max(Math.floor(t), 28), 110);
}

function calcCam(px, py, tile, cols, rows) {
  const vw = window.innerWidth, vh = window.innerHeight;
  const mapW = cols * tile, mapH = rows * tile;
  const rawX = px * tile + tile / 2 - vw / 2;
  const rawY = py * tile + tile / 2 - vh / 2;
  // If the map is smaller than the viewport on an axis, CENTER it
  // (negative camera) instead of leaving dead black space.
  const x = mapW <= vw ? -(vw - mapW) / 2 : Math.max(0, Math.min(mapW - vw, rawX));
  const y = mapH <= vh ? -(vh - mapH) / 2 : Math.max(0, Math.min(mapH - vh, rawY));
  return { x, y };
}

// Deterministic per-tile hash — stable visual variation
// (cracks, runes, torch placement) that never flickers.
function tileHash(x, y) {
  let h = (x * 73856093) ^ (y * 19349663);
  h = (h ^ (h >> 13)) >>> 0;
  return h % 10007;
}

// ─────────────────────────────────────────────────────────────
// RUN PERSISTENCE — localStorage, per profile, v2 key
// ─────────────────────────────────────────────────────────────
const runKey = (profile) =>
  `terror_dungeon1_v2_${profile?.uid || profile?.name || "anon"}`;

function loadRun(profile) {
  try {
    const raw = localStorage.getItem(runKey(profile));
    if (!raw) return null;
    const d = JSON.parse(raw);
    return {
      ...d,
      secretsFound: new Set(d.secretsFound || []),
      solved: d.solved || {},
      litFlames: d.litFlames || [],
      litBraziers: d.litBraziers || [],
    };
  } catch (e) { return null; }
}

function saveRun(profile, state) {
  try {
    localStorage.setItem(runKey(profile), JSON.stringify({
      ...state,
      secretsFound: Array.from(state.secretsFound),
    }));
  } catch (e) {}
}

// ═════════════════════════════════════════════════════════════
// ILK SIGILS — one consistent SVG icon set used EVERYWHERE
// (flames, mural, tablet, puzzle UI). If a sigil appears in the
// world, it is meaningful. Decorative noise was removed.
// ═════════════════════════════════════════════════════════════
function IlkSigil({ ilk, size = 16, lit = true }) {
  const c = lit ? ilk.color : "#3a2f55";
  const inner = {
    // SPACE — four-point star
    space: (
      <path d="M12 1 L14.6 9.4 L23 12 L14.6 14.6 L12 23 L9.4 14.6 L1 12 L9.4 9.4 Z" />
    ),
    // TIME — hourglass
    time: (
      <path d="M5 2 H19 V5 L13.5 12 L19 19 V22 H5 V19 L10.5 12 L5 5 Z M7.6 4 L12 9.6 L16.4 4 Z" fillRule="evenodd" />
    ),
    // PHANTOM — shade / mask
    phantom: (
      <>
        <path d="M12 2 C6.5 2 4.5 6.5 4.5 11.5 V21.5 L7.2 18.8 L9.6 21.5 L12 18.8 L14.4 21.5 L16.8 18.8 L19.5 21.5 V11.5 C19.5 6.5 17.5 2 12 2 Z" />
        <circle cx="9.3" cy="10.5" r="1.5" fill="#000" />
        <circle cx="14.7" cy="10.5" r="1.5" fill="#000" />
      </>
    ),
    // DRAGON — jagged fang-flame
    dragon: (
      <path d="M12 1 L19.5 7.5 L15.8 8.6 L19 14.5 L13.4 12.6 L12 23 L10.6 12.6 L5 14.5 L8.2 8.6 L4.5 7.5 Z" />
    ),
  }[ilk.id];
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{
      display: "block",
      filter: lit ? `drop-shadow(0 0 ${Math.max(2, size * 0.22)}px ${ilk.glow})` : "none",
      opacity: lit ? 1 : 0.55,
    }}>
      <g fill={c} stroke={lit ? ilk.glow : "none"} strokeWidth="0.4">{inner}</g>
    </svg>
  );
}

// ═════════════════════════════════════════════════════════════
// SMALL SPRITES (CSS scaffolds — swap to CDN webp later)
// ═════════════════════════════════════════════════════════════

function FlamePedestal({ flame, state, tile, onClick }) {
  // state: "unlit" | "lit" | "corrupt"
  const lit = state === "lit", bad = state === "corrupt";
  const col = bad ? THEME.corrupt : flame.ilk.color;
  const glow = bad ? THEME.corrupt : flame.ilk.glow;
  return (
    <div onClick={onClick} style={{
      position: "absolute", left: flame.x * tile, top: flame.y * tile,
      width: tile, height: tile, zIndex: 6, cursor: "pointer",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {/* pedestal */}
      <div style={{
        position: "absolute", bottom: tile * 0.08, width: tile * 0.5, height: tile * 0.34,
        background: `linear-gradient(180deg, ${THEME.stone.highlight}, ${THEME.stone.wall})`,
        border: `1px solid ${THEME.borderCool}`, borderRadius: 3,
      }} />
      {/* flame */}
      <div style={{
        position: "absolute", bottom: tile * 0.36,
        width: tile * 0.34, height: tile * 0.44,
        borderRadius: "50% 50% 45% 45% / 70% 70% 30% 30%",
        background: (lit || bad)
          ? `radial-gradient(ellipse at 50% 80%, ${col}, ${glow}55 70%, transparent)`
          : "radial-gradient(ellipse at 50% 80%, #1a1a2455, transparent)",
        boxShadow: (lit || bad) ? `0 0 ${tile * 0.4}px ${glow}88` : "none",
        animation: (lit || bad) ? "flameFlicker 0.9s ease-in-out infinite alternate" : "none",
        opacity: (lit || bad) ? 1 : 0.45,
      }} />
      {/* ilk sigil on pedestal — consistent icon language */}
      <div style={{ position: "absolute", bottom: tile * 0.1 }}>
        <IlkSigil ilk={flame.ilk} size={tile * 0.3} lit={lit || bad} />
      </div>
    </div>
  );
}

function BrazierSprite({ x, y, tile, lit }) {
  return (
    <div style={{
      position: "absolute", left: x * tile, top: y * tile,
      width: tile, height: tile, zIndex: 6,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        position: "absolute", bottom: tile * 0.06, width: tile * 0.4, height: tile * 0.5,
        background: `linear-gradient(180deg, #1a1424, ${THEME.stone.wall})`,
        border: `1px solid ${THEME.borderCool}`,
        clipPath: "polygon(20% 0, 80% 0, 95% 100%, 5% 100%)",
      }} />
      {lit && (
        <div style={{
          position: "absolute", bottom: tile * 0.42,
          width: tile * 0.4, height: tile * 0.5,
          borderRadius: "50% 50% 45% 45% / 70% 70% 30% 30%",
          background: `radial-gradient(ellipse at 50% 85%, ${THEME.torch}, #ff8c0055 65%, transparent)`,
          boxShadow: `0 0 ${tile * 0.7}px ${THEME.torch}99`,
          animation: "flameFlicker 0.8s ease-in-out infinite alternate",
        }} />
      )}
    </div>
  );
}

function SealSprite({ x, y, tile, state, roman, revealed }) {
  // state: "idle" | "good"
  const good = state === "good";
  return (
    <div style={{
      position: "absolute", left: x * tile + tile * 0.12, top: y * tile + tile * 0.12,
      width: tile * 0.76, height: tile * 0.76, zIndex: 4,
      borderRadius: 6, pointerEvents: "none",
      border: `2px solid ${good ? ILKS.SPACE.glow : THEME.borderCool}`,
      background: good
        ? `radial-gradient(circle, ${ILKS.SPACE.color}44, transparent)`
        : `radial-gradient(circle, ${THEME.stone.deepest}, ${THEME.stone.base})`,
      boxShadow: good ? `0 0 12px ${ILKS.SPACE.glow}88` : "inset 0 0 6px #000",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: tile * 0.3, color: revealed ? (good ? "#fff" : THEME.mortarHot) : "transparent",
      textShadow: revealed ? `0 0 6px ${THEME.mortarHot}` : "none",
      transition: "all 0.3s",
    }}>{roman}</div>
  );
}

function WarpSprite({ x, y, tile, color }) {
  return (
    <div style={{
      position: "absolute", left: x * tile, top: y * tile,
      width: tile, height: tile, zIndex: 4, pointerEvents: "none",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {/* outer ring — dashed, spinning */}
      <div style={{
        position: "absolute", width: "82%", height: "82%", borderRadius: "50%",
        border: `2px dashed ${color}cc`,
        boxShadow: `0 0 12px ${color}55`,
        animation: "warpSpin 5s linear infinite",
      }} />
      {/* inner ring — counter-spin */}
      <div style={{
        position: "absolute", width: "54%", height: "54%", borderRadius: "50%",
        border: `1px solid ${color}`,
        animation: "warpSpin 3s linear infinite reverse",
      }} />
      {/* core */}
      <div style={{
        position: "absolute", width: "26%", height: "26%", borderRadius: "50%",
        background: `radial-gradient(circle, #fff, ${color} 60%, transparent)`,
        boxShadow: `0 0 ${tile * 0.3}px ${color}`,
        animation: "breathe 1.8s ease-in-out infinite",
      }} />
    </div>
  );
}

function ChestSprite({ x, y, tile, open }) {
  const wood = open ? "#241a30" : "#3a2410";
  const woodD = open ? "#140e1e" : "#1f1206";
  const gold = open ? "#6a5a3a" : "#c9a84c";
  return (
    <div style={{
      position: "absolute", left: x * tile + tile * 0.12, top: y * tile + tile * 0.14,
      width: tile * 0.76, height: tile * 0.66, zIndex: 6, pointerEvents: "none",
      filter: open ? "none" : "drop-shadow(0 0 6px #c9a84c44)",
    }}>
      {/* lid */}
      <div style={{
        position: "absolute", top: open ? "-18%" : 0, left: 0, right: 0, height: "42%",
        background: `linear-gradient(180deg, ${wood}, ${woodD})`,
        border: `2px solid ${gold}`, borderBottom: "none",
        borderRadius: "40% 40% 0 0 / 80% 80% 0 0",
        transform: open ? "rotate(-8deg)" : "none",
        transformOrigin: "left bottom", transition: "all 0.3s",
      }} />
      {/* base */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, height: "58%",
        background: `linear-gradient(180deg, ${wood}, ${woodD})`,
        border: `2px solid ${gold}`, borderRadius: "0 0 3px 3px",
      }}>
        {/* gold band */}
        <div style={{
          position: "absolute", left: "44%", top: 0, bottom: 0, width: "12%",
          background: gold, opacity: 0.9,
        }} />
        {/* lock */}
        {!open && (
          <div style={{
            position: "absolute", left: "50%", top: "-22%", transform: "translateX(-50%)",
            width: tile * 0.14, height: tile * 0.16,
            background: `linear-gradient(180deg, #ffd97a, ${gold})`,
            border: "1px solid #00000088", borderRadius: 2,
          }} />
        )}
      </div>
      {/* open glow */}
      {open && (
        <div style={{
          position: "absolute", left: "20%", right: "20%", top: "10%", height: "40%",
          background: `radial-gradient(ellipse, ${THEME.mortarHot}55, transparent 70%)`,
        }} />
      )}
    </div>
  );
}

function LeverSprite({ x, y, tile, pulled }) {
  return (
    <div style={{
      position: "absolute", left: x * tile, top: y * tile,
      width: tile, height: tile, zIndex: 6, pointerEvents: "none",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        width: tile * 0.4, height: tile * 0.5,
        background: `linear-gradient(180deg, ${THEME.stone.highlight}, ${THEME.stone.wall})`,
        border: `1px solid ${pulled ? ILKS.SPACE.glow : THEME.borderCool}`,
        borderRadius: 4, position: "relative",
        boxShadow: pulled ? `0 0 10px ${ILKS.SPACE.glow}66` : "none",
      }}>
        <div style={{
          position: "absolute", left: "50%", top: "50%",
          width: 3, height: tile * 0.34,
          background: pulled ? ILKS.SPACE.glow : "#888",
          transformOrigin: "bottom center",
          transform: `translate(-50%,-100%) rotate(${pulled ? -40 : 40}deg)`,
          borderRadius: 2, transition: "transform 0.25s",
        }} />
      </div>
    </div>
  );
}

function BossFigure({ boss, tile, defeated }) {
  return (
    <div style={{
      position: "absolute", left: boss.x * tile, top: boss.y * tile - tile * 0.4,
      width: tile, height: tile * 1.4, zIndex: 7, pointerEvents: "none",
      display: "flex", alignItems: "flex-end", justifyContent: "center",
      opacity: defeated ? 0.35 : 1, transition: "opacity 0.6s",
    }}>
      <div style={{
        width: tile * 0.66, height: tile * 1.2,
        background: `linear-gradient(180deg, ${boss.color}33, ${THEME.stone.deepest})`,
        border: `2px solid ${boss.color}${defeated ? "44" : "cc"}`,
        borderRadius: "40% 40% 20% 20%",
        boxShadow: defeated ? "none" : `0 0 ${tile * 0.5}px ${boss.color}55`,
        animation: defeated ? "none" : "breathe 3s ease-in-out infinite",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: tile * 0.4, color: boss.color,
        textShadow: `0 0 8px ${boss.color}`,
      }}>◈</div>
    </div>
  );
}

function TabletSprite({ x, y, tile }) {
  return (
    <div style={{
      position: "absolute", left: x * tile, top: y * tile,
      width: tile, height: tile, zIndex: 6, pointerEvents: "none",
      display: "flex", alignItems: "center", justifyContent: "center",
      animation: "tabletPulse 2.5s ease-in-out infinite",
    }}>
      <div style={{
        width: "82%", height: "82%",
        background: "linear-gradient(135deg, #1a0820, #0d0410)",
        border: "2px solid #dc262688", borderRadius: 4,
        display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr",
        padding: 3, gap: 2,
      }}>
        {[ILKS.SPACE, ILKS.TIME, ILKS.PHANTOM, ILKS.DRAGON].map((ilk, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <IlkSigil ilk={ilk} size={tile * 0.2} lit={true} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// HUD / TRANSITIONS
// ═════════════════════════════════════════════════════════════

function DungeonHUD({ floorMeta, strikes, crests, onExit, mobile, cleared, secretsFound, secretsTotal }) {
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 60,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: mobile ? "8px 10px" : "10px 18px",
      background: "linear-gradient(180deg, rgba(0,0,0,0.85), transparent)",
      pointerEvents: "none", fontFamily: SD.fontDisplay,
    }}>
      <button onClick={onExit} style={{
        pointerEvents: "auto", cursor: "pointer",
        background: "rgba(0,0,0,0.6)", color: SD.starDim,
        border: `1px solid ${THEME.borderCool}`, borderRadius: 8,
        padding: "5px 12px", fontSize: 11, letterSpacing: 2,
        fontFamily: SD.fontDisplay,
      }}>◂ LEAVE</button>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 10, color: THEME.mortarHot, letterSpacing: 3 }}>
          {floorMeta.label}
        </div>
        <div style={{ fontSize: mobile ? 11 : 13, color: "#e8e0f0", letterSpacing: 2, textShadow: "0 0 12px #7c3aed66" }}>
          <span style={{ color: "#c9a84c", marginRight: 8 }}>◆</span>
          {floorMeta.title.toUpperCase()}
          <span style={{ color: "#c9a84c", marginLeft: 8 }}>◆</span>
        </div>
        <div style={{
          margin: "3px auto 0", width: 120, height: 1,
          background: "linear-gradient(90deg, transparent, #c9a84c88, transparent)",
        }} />
      </div>
      <div style={{ textAlign: "right", fontSize: 11 }}>
        {cleared && (
          <div style={{
            color: "#16a34a", fontSize: 9, letterSpacing: 2,
            textShadow: "0 0 8px #16a34a66",
          }}>✓ CLEARED</div>
        )}
        <div style={{ color: "#c9a84c", display: "flex", alignItems: "center", gap: 4 }}>
          <ChocolateCoin size={11} /> {crests}
        </div>
        {/* Secrets counter — always visible so a player who beat the
            boss can still see the eggs they're missing. */}
        <div style={{
          color: secretsFound >= secretsTotal ? "#16a34a" : "#b9a8ff",
          letterSpacing: 1,
        }}>✶ {secretsFound}/{secretsTotal}</div>
        <div style={{ color: strikes > 0 ? "#dc2626" : SD.starDim, letterSpacing: 1 }}>
          {Array.from({ length: 3 }).map((_, i) => (i < strikes ? "✖" : "·")).join(" ")}
        </div>
      </div>
    </div>
  );
}

function FloorTitleCard({ meta, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 1600);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 300, background: "#000",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      animation: "titleFade 1.6s ease-in-out forwards", fontFamily: SD.fontDisplay,
    }}>
      <div style={{ fontSize: 14, color: THEME.mortarHot, letterSpacing: 6 }}>{meta.label}</div>
      <div style={{ fontSize: 26, color: "#e8e0f0", letterSpacing: 4, marginTop: 8, textShadow: "0 0 24px #7c3aed88" }}>
        {meta.title.toUpperCase()}
      </div>
      <div style={{
        margin: "14px 0", width: 220, height: 1,
        background: "linear-gradient(90deg, transparent, #c9a84c, transparent)",
        position: "relative",
      }}>
        <div style={{
          position: "absolute", left: "50%", top: -5, transform: "translateX(-50%)",
          color: "#c9a84c", fontSize: 10,
        }}>◆</div>
      </div>
      {meta.ambient && (
        <div style={{ fontSize: 11, color: "#8a80a0", letterSpacing: 1, fontStyle: "italic", maxWidth: 360, textAlign: "center", lineHeight: 1.7 }}>
          {meta.ambient}
        </div>
      )}
    </div>
  );
}

function WormholeFlash({ onComplete }) {
  useEffect(() => {
    const t = setTimeout(onComplete, 800);
    return () => clearTimeout(t);
  }, [onComplete]);
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 350, pointerEvents: "none",
      background: `radial-gradient(circle, ${THEME.corrupt}cc, #000 75%)`,
      animation: "wormholePulse 0.8s ease-out forwards",
    }} />
  );
}

// ═════════════════════════════════════════════════════════════
// VISUAL NOVEL CLOSEUP — murals, chests, bosses, tablet
// ═════════════════════════════════════════════════════════════
function ObjectCloseup({ title, accent, lines, options, onAction, onClose }) {
  const [lineIdx, setLineIdx] = useState(0);
  const done = lineIdx >= lines.length - 1;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      if (e.key === "Enter" || e.key === " " || e.key === "q" || e.key === "Q") {
        e.preventDefault();
        if (!done) setLineIdx(i => i + 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [done, onClose]);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.92)",
      display: "flex", flexDirection: "column", fontFamily: SD.fontDisplay,
    }}
      onClick={() => { if (!done) setLineIdx(i => i + 1); }}
    >
      {/* art panel — swap to CDN art later */}
      <div style={{
        flex: "0 0 42%", position: "relative",
        background: `radial-gradient(ellipse at center, ${accent}18 0%, #000 75%)`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <div style={{
          fontSize: 64, color: accent, textShadow: `0 0 24px ${accent}`,
          animation: "breathe 3s ease-in-out infinite",
        }}>◆</div>
        <div style={{
          position: "absolute", bottom: 14, left: 0, right: 0, textAlign: "center",
          fontSize: 13, letterSpacing: 4, color: accent,
        }}>{title.toUpperCase()}</div>
      </div>

      {/* dialogue panel */}
      <div style={{
        flex: 1, padding: "22px 26px", borderTop: `1px solid ${accent}44`,
        display: "flex", flexDirection: "column", justifyContent: "space-between",
      }}>
        <div>
          {lines.slice(0, lineIdx + 1).map((l, i) => (
            <div key={i} style={{
              fontSize: 14, lineHeight: 1.8, marginBottom: 6,
              color: l.startsWith("◆") ? "#c9a84c" : "#d8d0e8",
              opacity: i === lineIdx ? 1 : 0.55,
            }}>{l}</div>
          ))}
          {!done && (
            <div style={{ fontSize: 10, color: SD.starDim, marginTop: 8, letterSpacing: 2 }}>
              TAP / Q TO CONTINUE ▾
            </div>
          )}
        </div>
        {done && (
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            {options.map((opt, i) => (
              <button key={i}
                onClick={(e) => { e.stopPropagation(); onAction(opt.action); }}
                style={{
                  cursor: "pointer", fontFamily: SD.fontDisplay,
                  background: opt.primary ? `${accent}22` : "rgba(0,0,0,0.6)",
                  color: opt.primary ? "#fff" : SD.starDim,
                  border: `1px solid ${opt.primary ? accent : THEME.borderCool}`,
                  borderRadius: 10, padding: "10px 22px",
                  fontSize: 12, letterSpacing: 2,
                }}>◆ {opt.label} ◆</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// PUZZLE TABLET — the easter egg. Press ilk symbols in the
// REVERSE of the flame order: Space → Dragon → Time (no Phantom
// would be too cruel) — canonical solution below.
// Solution: reverse flame order minus the trap:
//   flames were Phantom→Time→Dragon→Space
//   "the order before the order, reversed" = Space→Dragon→Time
//   Phantom is the trap key this time; pressing it = instant wrong.
// ═════════════════════════════════════════════════════════════
const TABLET_SOLUTION = ["space", "dragon", "time"];
const TABLET_TRAP = "phantom";

function PuzzleTablet({ onCorrect, onWrong, onClose }) {
  const [pressed, setPressed] = useState([]);
  const [feedback, setFeedback] = useState(null);

  const handlePress = (id) => {
    if (feedback) return;
    if (id === TABLET_TRAP) {
      setFeedback("wrong");
      setTimeout(onWrong, 1100);
      return;
    }
    const next = [...pressed, id];
    setPressed(next);
    if (next[next.length - 1] !== TABLET_SOLUTION[next.length - 1]) {
      setFeedback("wrong");
      setTimeout(onWrong, 1100);
      return;
    }
    if (next.length === TABLET_SOLUTION.length) {
      setFeedback("correct");
      setTimeout(onCorrect, 1300);
    }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); onClose(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 400, background: "#000",
      display: "flex", flexDirection: "column", fontFamily: SD.fontDisplay,
    }}>
      <div style={{
        flex: "0 0 45%", position: "relative",
        background: feedback === "correct"
          ? "radial-gradient(ellipse at center, #16a34a33 0%, #000 70%)"
          : feedback === "wrong"
            ? "radial-gradient(ellipse at center, #dc262633 0%, #000 70%)"
            : "radial-gradient(ellipse at center, #1a040a 0%, #000 70%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "background 0.5s",
      }}>
        {/* tablet — swap: tablet_oath.webp */}
        <div style={{
          width: 180, height: 180,
          background: "linear-gradient(135deg, #1a0820, #0d0410)",
          border: "3px solid #dc262666", borderRadius: 8,
          display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr",
          padding: 12, gap: 8,
        }}>
          {[ILKS.SPACE, ILKS.TIME, ILKS.PHANTOM, ILKS.DRAGON].map(ilk => {
            const used = pressed.includes(ilk.id);
            return (
              <button aria-label={ilk.label} key={ilk.id} onClick={() => handlePress(ilk.id)} style={{
                cursor: "pointer", background: used ? `${ilk.color}33` : "rgba(0,0,0,0.5)",
                border: `2px solid ${ilk.color}${used ? "ff" : "66"}`,
                borderRadius: 6, fontFamily: SD.fontDisplay,
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.2s",
              }}><IlkSigil ilk={ilk} size={34} lit={true} /></button>
            );
          })}
        </div>
      </div>
      <div style={{
        flex: 1, padding: "20px 26px", borderTop: "1px solid #dc262644",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
      }}>
        <div style={{ fontSize: 13, color: "#d8d0e8", lineHeight: 1.8, textAlign: "center", maxWidth: 440 }}>
          {feedback === "correct" ? "The tablet drinks the order. Something very old exhales." :
           feedback === "wrong"   ? "The symbols go dark. The tablet forgets you were here." :
           "The cross remembers the order that came before the order. What lit the flames — walked backwards. And one of the four was never asked."}
        </div>
        <div style={{ fontSize: 10, color: SD.starDim, letterSpacing: 2 }}>
          {pressed.length} / {TABLET_SOLUTION.length} PRESSED
        </div>
        <button onClick={onClose} style={{
          cursor: "pointer", fontFamily: SD.fontDisplay,
          background: "rgba(0,0,0,0.6)", color: SD.starDim,
          border: `1px solid ${THEME.borderCool}`, borderRadius: 10,
          padding: "8px 20px", fontSize: 11, letterSpacing: 2,
        }}>◆ STEP BACK ◆</button>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════
export default function Dungeon1({ profile, onExit, onBossBattle }) {
  const gender = profile?.gender || "f";
  const mobile = isMobile();
  const companion = useEquippedCompanion(profile?.uid);

  // ── restore or start run ───────────────────────────────────
  const savedRef = useRef(undefined);
  if (savedRef.current === undefined) savedRef.current = loadRun(profile);
  const saved = savedRef.current;

  const [floorId, setFloorId] = useState(saved?.floor || "B1F");
  const floor = FLOORS[floorId];
  const meta = FLOOR_META[floorId];

  // Position is NOT restored — every session starts at the entrance
  // of the saved floor (puzzle progress still persists). Prevents
  // ever waking up inside geometry from a stale save.
  const [pos, setPos] = useState(() => {
    const f = FLOORS[saved?.floor || "B1F"];
    return { ...(f.ent.stairsUp || f.ent.stairsDown || { x: 1, y: 1 }) };
  });
  const [facing, setFacing] = useState("down");
  const [walkFrame, setWalkFrame] = useState(0);
  const [motion, setMotion] = useState("rest");   // walk | rest | idle
  const [idleStep, setIdleStep] = useState(0);
  const [tile, setTile] = useState(() => calcTile(floor.cols));

  // Firebase is the persistent truth for found secrets — merged into
  // the local run so found-state survives localStorage wipes and
  // device changes. (Stamped by recordDungeonSecret on collect.)
  const fbSecretMap = profile?.storyProgress?.dungeonSecrets?.[`d${DUNGEON_ID}`] || {};
  const fbSecretKey = Object.keys(fbSecretMap).sort().join(",");

  // Dungeon cleared? (T'yrent down + recordDungeonClear stamped.)
  const cleared = (profile?.storyProgress?.completedDungeons || []).includes(DUNGEON_ID);

  // ── dungeon state ──────────────────────────────────────────
  // NOTE: crestsEarned is SESSION-ONLY now (never restored from the
  // saved run). Crests are truly awarded to Firebase at collect time,
  // so restoring the old running total would double-count against the
  // fresh profile balance on the next visit.
  const [dungeonState, setDungeonState] = useState(() => saved ? {
    solved: saved.solved, litFlames: saved.litFlames,
    litBraziers: saved.litBraziers, sealProgress: saved.sealProgress || 0,
    secretsFound: new Set([...saved.secretsFound, ...Object.keys(fbSecretMap)]),
    muralReads: saved.muralReads || 0,
    tabletAttempts: saved.tabletAttempts || 0,
    clearShown: saved.clearShown || false,
    crestsEarned: 0,
  } : {
    solved: {},            // { B1F: true, ... }
    litFlames: [],         // flame ids in order lit
    litBraziers: [],       // "x,y" keys
    sealProgress: 0,       // how many seals stepped correctly
    secretsFound: new Set(Object.keys(fbSecretMap)),
    muralReads: 0,
    tabletAttempts: 0,
    clearShown: false,     // one-time dungeon-clear ceremony shown?
    crestsEarned: 0,
  });

  // Late-arriving / refreshed profile — union any new Firebase stamps
  // into the local set (idempotent; only updates if something is new).
  useEffect(() => {
    if (!fbSecretKey) return;
    setDungeonState(d => {
      const ids = fbSecretKey.split(",");
      if (ids.every(id => d.secretsFound.has(id))) return d;
      const s = new Set(d.secretsFound);
      ids.forEach(id => s.add(id));
      return { ...d, secretsFound: s };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fbSecretKey]);
  const [strikes, setStrikes] = useState(saved?.strikes || 0);

  const [dialogue, setDialogue] = useState(null);
  const [closeup, setCloseup] = useState(null);     // {title,accent,lines,options,handler}
  const [tabletOpen, setTabletOpen] = useState(false);
  const [titleCard, setTitleCard] = useState(meta); // show on first mount too
  const [wormhole, setWormhole] = useState(false);
  const [nearby, setNearby] = useState(null);
  const [sliding, setSliding] = useState(false);

  const keysRef = useRef({});
  const posRef = useRef(pos);
  posRef.current = pos;
  const floorRef = useRef(floorId);
  floorRef.current = floorId;
  const stateRef = useRef(dungeonState);
  stateRef.current = dungeonState;
  const lockRef = useRef(false);      // movement locked (closeup, slide, transitions)
  const warpStreakRef = useRef({ key: null, n: 0 });
  const justWarpedRef = useRef(false);
  const lastMoveRef = useRef(0);
  const slideDirRef = useRef(null);

  // Memoized — a fresh array here every render previously caused an
  // infinite render loop whenever the player stood near an
  // interactable (the proximity effect depended on it).
  const defeatedKey = (profile?.storyProgress?.defeatedOpponents || []).join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const defeated = useMemo(() => defeatedKey ? defeatedKey.split(",") : [], [defeatedKey]);

  // ── persistence ────────────────────────────────────────────
  useEffect(() => {
    saveRun(profile, {
      floor: floorId, pos, strikes, ...dungeonState,
    });
  }, [floorId, pos, strikes, dungeonState, profile]);

  // ── DUNGEON-CLEAR CEREMONY — one time, ever ────────────────
  // Fires on the remount right after T'yrent falls (the reward
  // screen returns the player here) — the in-dungeon "you actually
  // finished this" moment that was missing. Persisted via clearShown
  // so it never replays.
  useEffect(() => {
    if (!cleared || dungeonState.clearShown) return;
    const foundCount = SECRET_IDS.filter(id => dungeonState.secretsFound.has(id)).length;
    const allFound = foundCount >= SECRET_IDS.length;
    const t = setTimeout(() => {
      setDungeonState(d => ({ ...d, clearShown: true }));
      lockRef.current = true;
      setCloseup({
        title: "The Outskirts — Cleared", accent: "#16a34a",
        lines: [
          "The Last Flame burns low. The wardens stand aside.",
          "◆ OUTSKIRTS OF THE EMPERIUM — COMPLETE.",
          `✶ Secrets uncovered: ${foundCount}/${SECRET_IDS.length}.`,
          allFound
            ? "Nothing left buried. The Outskirts have given you everything."
            : "The stones still whisper. Something here has not been found.",
          "You may return whenever you wish — but the wardens remember you now. Rematches pay almost nothing.",
        ],
        options: [
          { label: "Keep exploring", action: "close", primary: true },
          { label: "Leave the Outskirts", action: "exit_dungeon" },
        ],
      });
    }, 1900); // let the floor title card finish first
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleared, dungeonState.clearShown]);

  // ── resize ─────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setTile(calcTile(FLOORS[floorRef.current].cols));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useEffect(() => { setTile(calcTile(floor.cols)); }, [floorId, floor.cols]);

  // ── walk → rest → idle ─────────────────────────────────────
  // A step sets "walk"; 140ms without another step drops to the
  // directional rest pose; 4s of that hands over to the true idle
  // cycle, whose frame order lives in SpriteManifest.
  useEffect(() => {
    const rest = setTimeout(() => setMotion("rest"), 140);
    const idle = setTimeout(() => { setMotion("idle"); setIdleStep(0); }, 4000);
    return () => { clearTimeout(rest); clearTimeout(idle); };
  }, [pos]);
  useEffect(() => {
    if (motion !== "idle") return;
    const iv = setInterval(() => setIdleStep(n => n + 1), 600);
    return () => clearInterval(iv);
  }, [motion]);

  // ── helpers ────────────────────────────────────────────────
  const toast = useCallback((text, color = THEME.mortarHot) => {
    setDialogue({ text, color });
  }, []);

  const cellAt = useCallback((f, x, y) => {
    const fl = FLOORS[f];
    if (y < 0 || y >= fl.rows || x < 0 || x >= fl.cols) return "#";
    return fl.grid[y][x];
  }, []);

  const doorOpen = useCallback((f) => !!stateRef.current.solved[f], []);

  const bossAt = useCallback((f, x, y) => {
    return Object.entries(BOSSES).find(([id, b]) =>
      b.floor === f && b.x === x && b.y === y && !defeated.includes(id)
    );
  }, [defeated]);

  const isSolid = useCallback((f, x, y) => {
    const c = cellAt(f, x, y);
    if (c === "#") return true;
    if (c === "F" || c === "C" || c === "M" || c === "L" || c === "B" || c === "O" || c === "T") return true;
    if (c === "D") return !doorOpen(f);
    if (bossAt(f, x, y)) return true;
    return false;
  }, [cellAt, doorOpen, bossAt]);

  // ── strikes / reset ────────────────────────────────────────
  const addStrike = useCallback(() => {
    setStrikes(s => {
      const n = s + 1;
      if (n >= 3) {
        // wormhole reset — current floor puzzle resets, player to floor entry
        setWormhole(true);
        const f = floorRef.current;
        setDungeonState(ds => ({
          ...ds,
          litFlames: f === "B1F" ? [] : ds.litFlames,
          sealProgress: f === "B3F" ? 0 : ds.sealProgress,
        }));
        setTimeout(() => {
          setPos({ ...FLOORS[floorRef.current].ent.stairsUp });
        }, 400);
        toast("The dungeon noticed. It put you back.", THEME.corrupt);
        return 0;
      }
      return n;
    });
  }, [toast]);

  // ── floor transitions ──────────────────────────────────────
  const goToFloor = useCallback((nextId, spawn) => {
    lockRef.current = true;
    setTitleCard(FLOOR_META[nextId]);
    setTimeout(() => {
      setFloorId(nextId);
      setPos(spawn);
      setNearby(null);
      lockRef.current = false;
    }, 700);
  }, []);

  // ── tile resolution after a step ───────────────────────────
  const resolveTile = useCallback((f, x, y, dir) => {
    const c = cellAt(f, x, y);
    const ds = stateRef.current;

    // STAIRS DOWN
    if (c === "S") {
      const idx = FLOOR_ORDER.indexOf(f);
      const next = FLOOR_ORDER[idx + 1];
      if (next) {
        goToFloor(next, { ...FLOORS[next].ent.stairsUp });
        toast(`Descending to ${FLOOR_META[next].label}…`);
      }
      return;
    }
    // STAIRS UP / EXIT
    if (c === "E") {
      if (f === "B1F") {
        setCloseup({
          title: "The Outer Gate", accent: THEME.mortarHot,
          lines: ["Cold air from above. The Plaza is up there.", "Leave the Outskirts?"],
          options: [
            { label: "Leave", action: "exit_dungeon", primary: true },
            { label: "Stay", action: "close" },
          ],
        });
      } else {
        const idx = FLOOR_ORDER.indexOf(f);
        const prev = FLOOR_ORDER[idx - 1];
        goToFloor(prev, { ...FLOORS[prev].ent.stairsDown });
      }
      return;
    }
    // VOID DRIFT — slide
    if (c === "~") {
      slideDirRef.current = dir;
      setSliding(true);
      return;
    }
    // WARP GLYPH
    if (c === "W") {
      if (justWarpedRef.current) return; // arrived here by warp; step off first
      const key = `${x},${y}`;
      const w = WARPS[key];
      if (!w || !w.dest) return;
      const streak = warpStreakRef.current;
      streak.n = streak.key === key ? streak.n + 1 : 1;
      streak.key = key;
      let dest = w.dest;
      if (w.secretAfter && streak.n >= w.secretAfter &&
          !ds.secretsFound.has("drift_secret_found")) {
        dest = w.secretDest;
        setDungeonState(d => {
          const s = new Set(d.secretsFound); s.add("drift_secret_found");
          return { ...d, secretsFound: s };
        });
        toast("The glyph hesitates… then takes you somewhere it shouldn't.", "#c9a84c");
      }
      justWarpedRef.current = true;
      lockRef.current = true;
      setWormhole(true);
      setTimeout(() => {
        setPos({ ...dest });
        lockRef.current = false;
      }, 350);
      return;
    }
    // SEAL PLATE (B3F)
    if (c === "s") {
      if (ds.solved.B3F) return;
      const allLit = FLOORS.B3F.ent.braziers.every(b =>
        ds.litBraziers.includes(`${b.x},${b.y}`));
      if (!allLit) { toast("Cold stone. The dark hasn't told you the order yet."); return; }
      const key = `${x},${y}`;
      const expected = SEAL_ORDER[ds.sealProgress];
      if (key === expected) {
        const n = ds.sealProgress + 1;
        if (n >= SEAL_ORDER.length) {
          setDungeonState(d => ({ ...d, sealProgress: n, solved: { ...d.solved, B3F: true } }));
          toast("Four seals. Right order. Somewhere, a door stops pretending.", ILKS.SPACE.glow);
        } else {
          setDungeonState(d => ({ ...d, sealProgress: n }));
          toast(`Seal ${SEAL_ROMAN[key]} accepts you. (${n}/4)`, ILKS.SPACE.glow);
        }
      } else if (SEAL_ORDER.slice(0, ds.sealProgress).includes(key)) {
        // re-stepping an already-correct seal: harmless
      } else {
        setDungeonState(d => ({ ...d, sealProgress: 0 }));
        toast("Wrong seal. The floor resets its count. It is not angry. It is counting.", THEME.corrupt);
        addStrike();
      }
      return;
    }
  }, [cellAt, goToFloor, toast, addStrike]);

  // ── slide engine ───────────────────────────────────────────
  useEffect(() => {
    if (!sliding) return;
    const iv = setInterval(() => {
      const f = floorRef.current;
      const { x, y } = posRef.current;
      const d = slideDirRef.current;
      const dx = d === "left" ? -1 : d === "right" ? 1 : 0;
      const dy = d === "up" ? -1 : d === "down" ? 1 : 0;
      const nx = x + dx, ny = y + dy;
      if (isSolid(f, nx, ny)) {
        setSliding(false);
        return;
      }
      setPos({ x: nx, y: ny });
      setWalkFrame(w => (w + 1) % 2);
      const c = cellAt(f, nx, ny);
      if (c !== "~") {
        setSliding(false);
        resolveTile(f, nx, ny, d);
      }
    }, 70);
    return () => clearInterval(iv);
  }, [sliding, isSolid, cellAt, resolveTile]);

  // ── keyboard ───────────────────────────────────────────────
  // e.code is physical-key based — immune to CapsLock, IME, and
  // keyboard layouts. e.key kept as fallback. Interact goes
  // through a ref so the handler is never stale.
  const CODE_MAP = {
    KeyW: "w", KeyA: "a", KeyS: "s", KeyD: "d",
    ArrowUp: "arrowup", ArrowDown: "arrowdown",
    ArrowLeft: "arrowleft", ArrowRight: "arrowright",
  };
  const tryInteractRef = useRef(() => {});
  useEffect(() => {
    const down = (e) => {
      const m = CODE_MAP[e.code] || (["w","a","s","d","arrowup","arrowdown","arrowleft","arrowright"].includes(e.key?.toLowerCase()) ? e.key.toLowerCase() : null);
      if (m) {
        e.preventDefault();
        keysRef.current[m] = true;
      }
      if ((e.code === "KeyQ" || e.code === "Enter" || e.key === "q" || e.key === "Enter")
          && !lockRef.current) {
        e.preventDefault();
        tryInteractRef.current();
      }
    };
    const up = (e) => {
      const m = CODE_MAP[e.code];
      if (m) keysRef.current[m] = false;
      if (e.key) keysRef.current[e.key.toLowerCase()] = false;
    };
    const clearAll = () => { keysRef.current = {}; };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", clearAll);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", clearAll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── movement loop ──────────────────────────────────────────
  const lockTicksRef = useRef(0);
  useEffect(() => {
    const iv = setInterval(() => {
      // watchdog: if locked but nothing is actually open, count down
      // and force-unlock (~2s). A stuck lock can never brick movement.
      if (lockRef.current && !closeup && !tabletOpen && !sliding) {
        lockTicksRef.current += 1;
        if (lockTicksRef.current > 50) {
          lockRef.current = false;
          lockTicksRef.current = 0;
        }
      } else {
        lockTicksRef.current = 0;
      }
      if (lockRef.current || sliding || closeup || tabletOpen) return;
      const k = keysRef.current;
      const now = Date.now();
      if (now - lastMoveRef.current < 130) return;
      let dir = null;
      if (k.w || k.arrowup) dir = "up";
      else if (k.s || k.arrowdown) dir = "down";
      else if (k.a || k.arrowleft) dir = "left";
      else if (k.d || k.arrowright) dir = "right";
      if (!dir) return;
      lastMoveRef.current = now;
      setMotion("walk");
      setFacing(dir);

      const f = floorRef.current;
      const { x, y } = posRef.current;
      const dx = dir === "left" ? -1 : dir === "right" ? 1 : 0;
      const dy = dir === "up" ? -1 : dir === "down" ? 1 : 0;
      const nx = x + dx, ny = y + dy;

      if (isSolid(f, nx, ny)) {
        // bumped into something interesting?
        const c = cellAt(f, nx, ny);
        if (c === "D" && !doorOpen(f)) toast(FLOOR_META[f].doorHint, THEME.corrupt);
        return;
      }
      // stepping off a warp clears the no-retrigger flag
      if (cellAt(f, x, y) === "W") justWarpedRef.current = false;
      // stepping onto a non-warp tile after a non-warp step breaks the streak
      if (cellAt(f, nx, ny) !== "W" && cellAt(f, x, y) !== "W") {
        warpStreakRef.current = { key: null, n: 0 };
      }
      setPos({ x: nx, y: ny });
      setWalkFrame(w => (w + 1) % 2);
      resolveTile(f, nx, ny, dir);
    }, 40);
    return () => clearInterval(iv);
  }, [sliding, closeup, tabletOpen, isSolid, cellAt, doorOpen, resolveTile, toast]);

  // ── proximity detection ────────────────────────────────────
  const setNearbySafe = useCallback((n) => {
    setNearby(prev => {
      if (prev === n) return prev;
      if (prev && n && prev.type === n.type && prev.label === n.label &&
          prev.x === n.x && prev.y === n.y && prev.verb === n.verb) return prev;
      return n;
    });
  }, []);
  useEffect(() => {
    if (closeup || tabletOpen) { setNearbySafe(null); return; }
    const f = floorId;
    const fl = FLOORS[f];
    const ds = dungeonState;
    const near = (ox, oy, r = 1.5) =>
      Math.abs(pos.x - ox) <= r && Math.abs(pos.y - oy) <= r;

    // bosses
    for (const [id, b] of Object.entries(BOSSES)) {
      if (b.floor !== f) continue;
      if (near(b.x, b.y, 1.6)) {
        setNearbySafe({ type: "boss", id, x: b.x, y: b.y,
                    verb: defeated.includes(id) ? "Speak" : "Face",
                    label: b.name, accentColor: b.color });
        return;
      }
    }
    // flames (B1F puzzle — Q or click both work)
    if (!ds.solved.B1F) {
      for (const flm of fl.ent.flames) {
        if (ds.litFlames.includes(flm.id)) continue;
        if (near(flm.x, flm.y, 1.6)) {
          setNearbySafe({ type: "flame", id: flm.id, x: flm.x, y: flm.y,
                      verb: "Light", label: `${flm.ilk.label} Flame`,
                      accentColor: flm.ilk.glow });
          return;
        }
      }
    }
    // mural
    if (fl.ent.mural && near(fl.ent.mural.x, fl.ent.mural.y)) {
      setNearbySafe({ type: "mural", x: fl.ent.mural.x, y: fl.ent.mural.y,
                  verb: "Read", label: "The Oath Mural", accentColor: "#c9a84c" });
      return;
    }
    // lever
    if (fl.ent.lever && near(fl.ent.lever.x, fl.ent.lever.y)) {
      setNearbySafe({ type: "lever", x: fl.ent.lever.x, y: fl.ent.lever.y,
                  verb: ds.solved.B2F ? "Look" : "Pull",
                  label: "Drift Lever", accentColor: ILKS.SPACE.glow });
      return;
    }
    // braziers
    for (const b of fl.ent.braziers) {
      if (near(b.x, b.y)) {
        const lit = ds.litBraziers.includes(`${b.x},${b.y}`);
        setNearbySafe({ type: "brazier", x: b.x, y: b.y,
                    verb: lit ? "Watch" : "Light", label: "Cold Brazier",
                    accentColor: THEME.torch });
        return;
      }
    }
    // chests
    for (const c of fl.ent.chests) {
      if (near(c.x, c.y)) {
        setNearbySafe({ type: "chest", x: c.x, y: c.y, verb: "Open",
                    label: "Chest", accentColor: "#c9a84c" });
        return;
      }
    }
    // tablet (only if revealed)
    if (fl.ent.tablet && ds.muralReads >= 2 && near(fl.ent.tablet.x, fl.ent.tablet.y)) {
      setNearbySafe({ type: "tablet", x: fl.ent.tablet.x, y: fl.ent.tablet.y,
                  verb: "Study", label: "The Oath Tablet", accentColor: "#dc2626" });
      return;
    }
    setNearbySafe(null);
  }, [pos, floorId, dungeonState, closeup, tabletOpen, defeated]);

  // ── interaction handler ────────────────────────────────────
  const tryInteract = useCallback(() => {
    const f = floorRef.current;
    const ds = stateRef.current;
    const { x, y } = posRef.current;

    // 1. hidden item under your feet (itemfinder feel — no bubble)
    const tKey = `${f}:${x},${y}`;
    const treasure = TREASURES[tKey];
    if (treasure && treasure.kind === "hidden" && !ds.secretsFound.has(treasure.id)) {
      collectTreasure(treasure);
      return;
    }
    // 2. whatever the proximity bubble points at
    if (nearby) openInteractable(nearby);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nearby]);
  tryInteractRef.current = tryInteract;

  const collectTreasure = useCallback((t) => {
    setDungeonState(d => {
      const s = new Set(d.secretsFound); s.add(t.id);
      return { ...d, secretsFound: s, crestsEarned: d.crestsEarned + (t.crests || 0) };
    });
    // REAL grant — atomic + idempotent in progression.js; a stamp that
    // already exists (e.g. found on another device) grants nothing twice.
    // Fire-and-forget: the dungeon never blocks on the network.
    const uid = profile?.uid || auth?.currentUser?.uid;
    recordDungeonSecret(uid, DUNGEON_ID, t.id, SECRET_REWARDS[t.id] || {})
      .catch(e => console.warn("[recordDungeonSecret]", t.id, "failed:", e));
    setCloseup({
      title: t.title, accent: "#c9a84c",
      lines: t.lines,
      options: [{ label: "Take it", action: "close", primary: true }],
    });
  }, [profile?.uid]);

  const handleFlameClickRef = useRef(() => {});
  const openInteractable = useCallback((n) => {
    const f = floorRef.current;
    const ds = stateRef.current;
    lockRef.current = true;

    if (n.type === "boss") {
      const b = BOSSES[n.id];
      const isDead = defeated.includes(n.id);
      const blocked = b.needs.some(req => !defeated.includes(req));
      if (isDead) {
        const epilogue = {
          suhnker: ["S'uhnker steps aside before you finish approaching.", '"Go. The deeper dark is worse company than I am."'],
          zhefir:  ["Z'hefir watches you pass.", "She says nothing. She has already said it with the cards."],
          tyrent:  ["T'yrent's flame burns low and steady now.", '"The Outskirts remember you, Aenarchist.", "Take what is yours. The Minerium has more doors."'],
        }[n.id];
        setCloseup({
          title: b.name, accent: b.color,
          lines: [
            ...epilogue,
            "◆ Already defeated. A rematch yields almost nothing — the Minerium does not pay twice.",
          ],
          options: [
            { label: "Leave", action: "close", primary: true },
            { label: "Rechallenge", action: `battle_${n.id}` },
          ],
        });
        return;
      }
      if (blocked && n.id === "tyrent") {
        setCloseup({
          title: b.name, accent: b.color,
          lines: [
            "He does not turn around.",
            '"You skipped my wardens."',
            '"The Last Flame does not burn for the impatient."',
            "Defeat S'uhnker and Z'hefir in the Watching Dark first.",
          ],
          options: [{ label: "Withdraw", action: "close", primary: true }],
        });
        return;
      }
      if (blocked && n.id === "zhefir") {
        setCloseup({
          title: b.name, accent: b.color,
          lines: ['"S\'uhnker first."', "She raises one finger. The corridor stays blocked."],
          options: [{ label: "Withdraw", action: "close", primary: true }],
        });
        return;
      }
      const intro = {
        suhnker: ["A figure in the dark corridor. He was waiting before you arrived.", '"The dark counts. I collect."', "S'uhnker draws his grimoire."],
        zhefir:  ["She is already shuffling.", '"S\'uhnker lost. That means you are interesting."', "Z'hefir draws her grimoire."],
        tyrent:  ["The Last Flame of the Minerium.", "He has been standing here longer than the stones.", '"One oath left. Yours or mine."', "T'yrent draws his grimoire."],
      }[n.id];
      setCloseup({
        title: b.name, accent: b.color, lines: intro,
        options: [
          { label: "Challenge", action: `battle_${n.id}`, primary: true },
          { label: "Withdraw", action: "close" },
        ],
      });
      return;
    }

    if (n.type === "flame") {
      lockRef.current = false; // flames don't open a panel
      const flm = FLOORS[f].ent.flames.find(fx => fx.id === n.id);
      if (flm) handleFlameClickRef.current(flm);
      return;
    }

    if (n.type === "mural") {
      const reads = ds.muralReads + 1;
      setDungeonState(d => ({ ...d, muralReads: reads }));
      const lines = [
        "Four ilks, carved in sequence. The stone still remembers the oath.",
        "◇ Phantom, first to swear.",
        "⟳ Time, second.",
        "◈ Dragon, third.",
        "✦ Space, last — and loudest.",
      ];
      if (reads === 2) lines.push("…you notice it for the first time: the symbols are also carved on the BACK of the slab. Reversed. Why would anyone carve them reversed?");
      if (reads > 2) lines.push("The reversed carving on the back stares at you. Somewhere below, something is shaped like an answer.");
      setCloseup({
        title: "The Oath Mural", accent: "#c9a84c", lines,
        options: [{ label: "Step back", action: "close", primary: true }],
      });
      return;
    }

    if (n.type === "lever") {
      if (ds.solved.B2F) {
        setCloseup({
          title: "Drift Lever", accent: ILKS.SPACE.glow,
          lines: ["Pulled. The door across the drift stands open."],
          options: [{ label: "Leave", action: "close", primary: true }],
        });
        return;
      }
      setDungeonState(d => ({ ...d, solved: { ...d.solved, B2F: true } }));
      setCloseup({
        title: "Drift Lever", accent: ILKS.SPACE.glow,
        lines: ["The lever fights you, then surrenders.", "Across the drift — iron grinding. A door remembering how to open.", "◆ The way down is open."],
        options: [{ label: "Good.", action: "close", primary: true }],
      });
      return;
    }

    if (n.type === "brazier") {
      const key = `${n.x},${n.y}`;
      if (ds.litBraziers.includes(key)) {
        setCloseup({
          title: "Brazier", accent: THEME.torch,
          lines: ["It burns. The dark keeps its distance.", BRAZIER_HINTS[key] || ""],
          options: [{ label: "Leave", action: "close", primary: true }],
        });
        return;
      }
      const lit = [...ds.litBraziers, key];
      setDungeonState(d => ({ ...d, litBraziers: lit }));
      const all = lit.length >= FLOORS.B3F.ent.braziers.length;
      setCloseup({
        title: "Brazier", accent: THEME.torch,
        lines: [
          "Flint. Spark. The flame takes.",
          BRAZIER_HINTS[key] || "",
          all ? "◆ All four braziers burn. The seals show their numbers now. Walk them in order."
              : `(${lit.length}/4 braziers lit)`,
        ],
        options: [{ label: "Leave", action: "close", primary: true }],
      });
      return;
    }

    if (n.type === "chest") {
      const tKey = `${floorRef.current}:${n.x},${n.y}`;
      const t = TREASURES[tKey];
      if (!t) { lockRef.current = false; return; }
      if (ds.secretsFound.has(t.id)) {
        setCloseup({
          title: t.title, accent: "#c9a84c",
          lines: ["Empty. You already took what mattered."],
          options: [{ label: "Leave", action: "close", primary: true }],
        });
        return;
      }
      if (t.needsBoss && !defeated.includes(t.needsBoss)) {
        setCloseup({
          title: t.title, accent: "#c9a84c",
          lines: ["Sealed. It only opens for whoever the flame no longer fears."],
          options: [{ label: "Leave", action: "close", primary: true }],
        });
        return;
      }
      collectTreasure(t);
      return;
    }

    if (n.type === "tablet") {
      const attempted = ds.tabletAttempts > 0;
      const solvedIt = ds.secretsFound.has("easter_egg_tablet");
      if (solvedIt) {
        setCloseup({
          title: "The Oath Tablet", accent: "#dc2626",
          lines: ["The tablet is still.", "You pressed the right symbols.", "The order before the order.", "The dragon remembers."],
          options: [{ label: "Leave", action: "close", primary: true }],
        });
        return;
      }
      setCloseup({
        title: "The Oath Tablet", accent: "#dc2626",
        lines: attempted
          ? ["The tablet is dark.", "Wrong order, last time.", "The cross remembers the order that came before the order — walked backwards.", "And one of the four was never asked."]
          : ["Four symbols. A cross.", "Space. Time. Phantom. Dragon.", "No instructions.", "But somewhere in this dungeon — you already have the answer."],
        options: [
          { label: "Press the symbols", action: "puzzle_tablet", primary: true },
          { label: "Leave", action: "close" },
        ],
      });
      return;
    }

    lockRef.current = false;
  }, [defeated, collectTreasure]);

  const handleCloseupAction = useCallback((action) => {
    setCloseup(null);
    lockRef.current = false;
    if (action === "close") return;
    if (action === "exit_dungeon") { onExit(); return; }
    if (action === "puzzle_tablet") { setTabletOpen(true); lockRef.current = true; return; }
    if (action.startsWith("battle_")) {
      const id = action.slice(7);
      onBossBattle(id);
      return;
    }
  }, [onExit, onBossBattle]);

  // ── flame puzzle (click) ───────────────────────────────────
  const handleFlameClick = useCallback((flame) => {
    const ds = stateRef.current;
    if (ds.solved.B1F) return;
    if (Math.abs(posRef.current.x - flame.x) > 2 || Math.abs(posRef.current.y - flame.y) > 2) {
      toast("Too far. Walk to the pedestal.");
      return;
    }
    if (ds.litFlames.includes(flame.id)) return;
    const expectedOrder = ds.litFlames.length + 1;
    if (flame.order === expectedOrder) {
      const lit = [...ds.litFlames, flame.id];
      if (lit.length >= 4) {
        setDungeonState(d => ({ ...d, litFlames: lit, solved: { ...d.solved, B1F: true } }));
        toast("The fourth flame takes. Far away, iron remembers how to move. ◆ The way down is open.", ILKS.SPACE.glow);
      } else {
        setDungeonState(d => ({ ...d, litFlames: lit }));
        toast(`${flame.ilk.label} accepts the spark. (${lit.length}/4)`, flame.ilk.glow);
      }
    } else {
      setDungeonState(d => ({ ...d, litFlames: [] }));
      toast("The flame turns violet, then dies. All of them die with it. The order matters.", THEME.corrupt);
      addStrike();
    }
  }, [toast, addStrike]);
  handleFlameClickRef.current = handleFlameClick;

  // ── tablet results ─────────────────────────────────────────
  const handleTabletCorrect = useCallback(() => {
    setTabletOpen(false);
    lockRef.current = false;
    setDungeonState(d => {
      const s = new Set(d.secretsFound); s.add("easter_egg_tablet");
      return { ...d, secretsFound: s };
    });
    // REAL grant — Chaos Dragon Lord in Solrend foil + the hidden
    // "Order Before the Order" achievement. Idempotent server-side.
    const uid = profile?.uid || auth?.currentUser?.uid;
    recordDungeonSecret(uid, DUNGEON_ID, "easter_egg_tablet", SECRET_REWARDS.easter_egg_tablet)
      .catch(e => console.warn("[recordDungeonSecret] tablet failed:", e));
    setCloseup({
      title: "The Oath Tablet", accent: "#16a34a",
      lines: [
        "The cross drinks the order. Stone splits along lines that were always there.",
        "Inside: a card that should not exist.",
        "◆ CHAOS DRAGON — EASTER EGG VARIANT — earned.",
        "(The rarest thing in the Outskirts. Maybe the rarest thing anywhere.)",
      ],
      options: [{ label: "Take it", action: "close", primary: true }],
    });
  }, [profile?.uid]);

  const handleTabletWrong = useCallback(() => {
    setTabletOpen(false);
    lockRef.current = false;
    setDungeonState(d => ({ ...d, tabletAttempts: d.tabletAttempts + 1 }));
    addStrike();
  }, [addStrike]);

  // ── B3F visibility ─────────────────────────────────────────
  const visibleAt = useCallback((x, y) => {
    if (!meta.dark) return 1;
    const dp = Math.hypot(x - pos.x, y - pos.y);
    let v = dp <= meta.visRadius ? 1 : dp <= meta.visRadius + 1.4 ? 0.35 : 0;
    for (const b of floor.ent.braziers) {
      if (!dungeonState.litBraziers.includes(`${b.x},${b.y}`)) continue;
      const db = Math.hypot(x - b.x, y - b.y);
      if (db <= 2.8) v = Math.max(v, 1);
      else if (db <= 4) v = Math.max(v, 0.35);
    }
    return v;
  }, [meta, pos, floor, dungeonState.litBraziers]);

  // ── render ─────────────────────────────────────────────────
  const TILE = tile;
  const cam = calcCam(pos.x, pos.y, TILE, floor.cols, floor.rows);
  const allBraziersLit = floorId === "B3F" &&
    floor.ent.braziers.every(b => dungeonState.litBraziers.includes(`${b.x},${b.y}`));

  return (
    <div style={{
      position: "fixed", inset: 0, background: "#000",
      overflow: "hidden", fontFamily: SD.fontDisplay, userSelect: "none",
    }}>
      <style>{`
        @keyframes flameFlicker { from { transform: scaleY(1); opacity: 0.9; } to { transform: scaleY(1.12); opacity: 1; } }
        @keyframes breathe { 0%,100% { opacity: 0.7; } 50% { opacity: 1; } }
        @keyframes warpSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes tabletPulse { 0%,100% { opacity: 0.6; } 50% { opacity: 1; } }
        @keyframes driftRing { 0% { box-shadow: inset 0 0 4px ${THEME.mortar}66; } 50% { box-shadow: inset 0 0 10px ${THEME.mortarHot}55; } 100% { box-shadow: inset 0 0 4px ${THEME.mortar}66; } }
        @keyframes titleFade { 0% { opacity: 1; } 60% { opacity: 1; } 100% { opacity: 0; pointer-events: none; } }
        @keyframes wormholePulse { 0% { opacity: 0; } 30% { opacity: 1; } 100% { opacity: 0; } }
        @keyframes hiddenTwinkle { 0%,92%,100% { opacity: 0; } 95% { opacity: 0.35; } }
        @keyframes runeBreathe { 0%,100% { opacity: 0.4; } 50% { opacity: 1; } }
        @keyframes targetPulse { 0%,100% { opacity: 0.5; transform: scale(1); } 50% { opacity: 1; transform: scale(1.06); } }
        @keyframes gatePulse { 0%,100% { box-shadow: 0 0 8px ${THEME.corrupt}33, inset 0 0 10px #000; } 50% { box-shadow: 0 0 18px ${THEME.corrupt}77, inset 0 0 10px #000; } }
        @keyframes poolFlicker { from { opacity: 0.7; } to { opacity: 1; } }
        @keyframes dustDrift { 0% { transform: translate(0,0); opacity: 0; } 15% { opacity: 0.5; } 85% { opacity: 0.5; } 100% { transform: translate(-40px,-90px); opacity: 0; } }
      `}</style>

      <DungeonHUD
        floorMeta={meta} strikes={strikes}
        crests={(profile?.chocolateCoins || 0) + dungeonState.crestsEarned}
        onExit={onExit} mobile={mobile}
        cleared={cleared}
        secretsFound={SECRET_IDS.filter(id => dungeonState.secretsFound.has(id)).length}
        secretsTotal={SECRET_IDS.length}
      />

      {/* ── MAP ── */}
      <div style={{
        position: "absolute",
        transform: `translate(${-cam.x}px, ${-cam.y}px)`,
        width: floor.cols * TILE, height: floor.rows * TILE,
      }}>
        {/* ── TILES — visual overhaul ──────────────────────────
            Walls are RAISED: a wall whose south neighbor is
            walkable shows a FRONT FACE (carved brick courses,
            torch sconces); otherwise it shows a dark TOP CAP.
            Floors are big worn slabs with deterministic
            cracks/runes. Everything hash-seeded so it never
            flickers between renders.                         */}
        {floor.grid.map((row, y) => row.map((c, x) => {
          const vis = visibleAt(x, y);
          const key = `${x}_${y}`;
          const h = tileHash(x, y);

          if (c === "#") {
            const below = y + 1 < floor.rows ? floor.grid[y + 1][x] : "#";
            const isFace = below !== "#";           // wall front face
            const hasTorch = isFace && h % 5 === 0; // sconce on some faces
            return (
              <div key={key} style={{
                position: "absolute", left: x * TILE, top: y * TILE,
                width: TILE, height: TILE,
                background: isFace
                  ? `repeating-linear-gradient(180deg,
                       #261a48 0px, #261a48 ${TILE * 0.22}px,
                       ${THEME.mortar}88 ${TILE * 0.22}px, ${THEME.mortar}88 ${TILE * 0.26}px,
                       #1c1138 ${TILE * 0.26}px, #1c1138 ${TILE * 0.48}px,
                       ${THEME.mortar}88 ${TILE * 0.48}px, ${THEME.mortar}88 ${TILE * 0.52}px)`
                  : `linear-gradient(135deg, #2e2050 0%, #221540 45%, #1a0f34 100%)`,
                borderTop: isFace ? `${Math.max(2, TILE * 0.1)}px solid #4a3a78` : `1px solid #4a3a7855`,
                borderLeft: isFace ? "none" : `1px solid #3a2a6044`,
                borderBottom: isFace ? `2px solid #000` : "none",
                boxShadow: isFace
                  ? `inset ${h % 2 ? 2 : -2}px 0 6px #00000088, inset 0 -${TILE * 0.18}px ${TILE * 0.2}px #000000aa`
                  : `inset 1px 1px 0 #5a4a8833, inset -2px -2px ${TILE * 0.2}px #00000077`,
                opacity: meta.dark ? Math.max(vis, 0.05) : 1,
                zIndex: 1,
              }}>
                {/* rune crack — glowing fissure on some faces */}
                {isFace && h % 7 === 3 && (
                  <div style={{
                    position: "absolute", left: "30%", top: "15%",
                    width: 2, height: "60%",
                    background: `linear-gradient(180deg, transparent, ${THEME.mortarHot}aa, transparent)`,
                    boxShadow: `0 0 6px ${THEME.mortarHot}`,
                    transform: `rotate(${(h % 3 - 1) * 14}deg)`,
                    animation: "runeBreathe 4s ease-in-out infinite",
                  }} />
                )}
                {/* torch sconce + flame */}
                {hasTorch && !meta.dark && (
                  <>
                    <div style={{
                      position: "absolute", left: "50%", bottom: "8%",
                      transform: "translateX(-50%)",
                      width: TILE * 0.1, height: TILE * 0.3,
                      background: "#1a0e06", border: "1px solid #00000088",
                    }} />
                    <div style={{
                      position: "absolute", left: "50%", bottom: "32%",
                      transform: "translateX(-50%)",
                      width: TILE * 0.26, height: TILE * 0.34,
                      borderRadius: "50% 50% 40% 40% / 75% 75% 25% 25%",
                      background: `radial-gradient(ellipse at 50% 85%, #ffd9a0, ${THEME.torch} 45%, transparent 80%)`,
                      boxShadow: `0 0 ${TILE * 0.5}px ${THEME.torch}aa`,
                      animation: `flameFlicker ${0.7 + (h % 4) * 0.13}s ease-in-out infinite alternate`,
                      zIndex: 2,
                    }} />
                  </>
                )}
              </div>
            );
          }

          const isSlide = c === "~";
          const isDoor = c === "D";
          const open = isDoor && doorOpen(floorId);

          // ── SEALED GATE / OPEN GATE ──
          if (isDoor) {
            return (
              <div key={key} style={{
                position: "absolute", left: x * TILE, top: y * TILE,
                width: TILE, height: TILE, zIndex: 3,
                background: open
                  ? `radial-gradient(ellipse at 50% 30%, ${THEME.mortarHot}22, ${THEME.stone.deepest} 75%)`
                  : `linear-gradient(180deg, #241236, #0e0618)`,
                border: open ? `1px solid ${THEME.borderHot}` : `2px solid ${THEME.corrupt}66`,
                boxShadow: open
                  ? `inset 0 0 ${TILE * 0.4}px ${THEME.mortarHot}33`
                  : `0 0 ${TILE * 0.3}px ${THEME.corrupt}44, inset 0 0 ${TILE * 0.3}px #000`,
                display: "flex", alignItems: "center", justifyContent: "center",
                opacity: meta.dark ? Math.max(vis, 0.05) : 1,
                animation: open ? "none" : "gatePulse 2.6s ease-in-out infinite",
              }}>
                {!open && (
                  <div style={{
                    width: "70%", height: "78%",
                    background: `repeating-linear-gradient(90deg, #1a0e2a 0 4px, #0a0414 4px 8px)`,
                    border: `1px solid ${THEME.corrupt}88`, borderRadius: "4px 4px 0 0",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: TILE * 0.3, color: THEME.corrupt,
                    textShadow: `0 0 8px ${THEME.corrupt}`,
                  }}>⊠</div>
                )}
              </div>
            );
          }

          // ── STAIRWELLS ──
          if (c === "S" || c === "E") {
            const down = c === "S";
            return (
              <div key={key} style={{
                position: "absolute", left: x * TILE, top: y * TILE,
                width: TILE, height: TILE, zIndex: 3,
                background: down
                  ? `linear-gradient(180deg, ${THEME.stone.mid}, #000 85%)`
                  : `linear-gradient(0deg, ${THEME.stone.mid}, ${THEME.mortarHot}22 90%)`,
                border: `1px solid ${THEME.borderHot}`,
                boxShadow: `inset 0 0 ${TILE * 0.5}px #000, 0 0 ${TILE * 0.25}px ${THEME.mortarHot}33`,
                opacity: meta.dark ? Math.max(vis, 0.05) : 1,
                overflow: "hidden",
              }}>
                {[0.15, 0.38, 0.61].map((t, i) => (
                  <div key={i} style={{
                    position: "absolute", left: `${12 + i * 8}%`, right: `${12 + i * 8}%`,
                    top: `${t * 100}%`, height: "14%",
                    background: down
                      ? `linear-gradient(180deg, ${THEME.stone.highlight}, ${THEME.stone.deepest})`
                      : `linear-gradient(0deg, ${THEME.stone.highlight}, ${THEME.stone.deepest})`,
                    borderBottom: "1px solid #000",
                  }} />
                ))}
                <div style={{
                  position: "absolute", inset: 0, display: "flex",
                  alignItems: "center", justifyContent: "center",
                  fontSize: TILE * 0.3, color: THEME.mortarHot,
                  textShadow: `0 0 8px ${THEME.mortarHot}`,
                  animation: "breathe 2.5s ease-in-out infinite",
                }}>{down ? "▼" : "▲"}</div>
              </div>
            );
          }

          // ── VOID DRIFT ──
          if (isSlide) {
            return (
              <div key={key} style={{
                position: "absolute", left: x * TILE, top: y * TILE,
                width: TILE, height: TILE,
                background: `radial-gradient(circle at ${30 + h % 40}% ${30 + (h >> 2) % 40}%, #14082e, #050112 70%)`,
                boxShadow: `inset 0 0 ${TILE * 0.3}px #000`,
                animation: `driftRing ${2.5 + (h % 5) * 0.4}s ease-in-out infinite`,
                opacity: meta.dark ? Math.max(vis, 0.04) : 1,
              }}>
                {/* drifting star speck */}
                {h % 3 === 0 && (
                  <div style={{
                    position: "absolute",
                    left: `${15 + h % 60}%`, top: `${15 + (h >> 3) % 60}%`,
                    width: 2, height: 2, borderRadius: "50%",
                    background: "#b9a8ff",
                    boxShadow: "0 0 4px #b9a8ff",
                    animation: `breathe ${2 + h % 3}s ease-in-out infinite`,
                  }} />
                )}
              </div>
            );
          }

          // ── FLOOR SLABS — darker than walls, with ambient
          //    occlusion shadows on any side that touches a wall ──
          const tone = h % 4; // slab shade variation
          const wUp = y > 0 && floor.grid[y - 1][x] === "#";
          const wDn = y + 1 < floor.rows && floor.grid[y + 1][x] === "#";
          const wLf = x > 0 && floor.grid[y][x - 1] === "#";
          const wRt = x + 1 < floor.cols && floor.grid[y][x + 1] === "#";
          const ao = [
            wUp && `inset 0 ${TILE * 0.28}px ${TILE * 0.25}px -${TILE * 0.12}px #000000cc`,
            wDn && `inset 0 -${TILE * 0.18}px ${TILE * 0.2}px -${TILE * 0.1}px #00000099`,
            wLf && `inset ${TILE * 0.2}px 0 ${TILE * 0.2}px -${TILE * 0.1}px #000000aa`,
            wRt && `inset -${TILE * 0.2}px 0 ${TILE * 0.2}px -${TILE * 0.1}px #000000aa`,
          ].filter(Boolean).join(", ");
          return (
            <div key={key} style={{
              position: "absolute", left: x * TILE, top: y * TILE,
              width: TILE, height: TILE,
              background: `linear-gradient(${h % 2 ? 135 : 45}deg,
                ${["#070312", "#090416", "#06020f", "#080313"][tone]} 0%,
                #030108 100%)`,
              boxShadow: `inset 1px 1px 0 ${THEME.stone.highlight}18, inset -1px -1px 0 #00000066${ao ? ", " + ao : ""}`,
              opacity: meta.dark ? Math.max(vis, 0.04) : 1,
              transition: meta.dark ? "opacity 0.25s" : "none",
            }}>
              {/* slab crack */}
              {h % 9 === 4 && (
                <div style={{
                  position: "absolute", left: "20%", top: "45%",
                  width: "55%", height: 1,
                  background: "#00000099",
                  transform: `rotate(${(h % 5 - 2) * 16}deg)`,
                }} />
              )}
              {/* (decorative slab runes removed — if a symbol is
                  visible on this floor, it MEANS something) */}
            </div>
          );
        }))}

        {/* ── TORCH LIGHT POOLS — warm glow cast onto the floor
            below each sconce ── */}
        {!meta.dark && floor.grid.map((row, y) => row.map((c, x) => {
          if (c !== "#") return null;
          const below = y + 1 < floor.rows ? floor.grid[y + 1][x] : "#";
          if (below === "#" || tileHash(x, y) % 5 !== 0) return null;
          return (
            <div key={`tp${x}_${y}`} style={{
              position: "absolute",
              left: (x - 1) * TILE, top: (y + 0.4) * TILE,
              width: TILE * 3, height: TILE * 2.4,
              background: `radial-gradient(ellipse at 50% 30%, ${THEME.torchPool}, transparent 65%)`,
              zIndex: 2, pointerEvents: "none",
              animation: `poolFlicker ${1.4 + (tileHash(x, y) % 3) * 0.3}s ease-in-out infinite alternate`,
            }} />
          );
        }))}

        {/* hidden item twinkle — nearly invisible, FireRed honest */}
        {floor.ent.hidden.map((h, i) => {
          const t = TREASURES[`${floorId}:${h.x},${h.y}`];
          if (t && dungeonState.secretsFound.has(t.id)) return null;
          return <div key={`h${i}`} style={{
            position: "absolute", left: h.x * TILE + TILE * 0.4, top: h.y * TILE + TILE * 0.4,
            width: TILE * 0.2, height: TILE * 0.2, zIndex: 3, pointerEvents: "none",
            background: `radial-gradient(circle, #fff, transparent)`,
            borderRadius: "50%", opacity: 0,
            animation: `hiddenTwinkle ${6 + i * 1.7}s linear infinite`,
          }} />;
        })}

        {/* warp glyphs */}
        {floor.ent.warps.map((w, i) => (
          <WarpSprite key={`w${i}`} x={w.x} y={w.y} tile={TILE} color={w.color || THEME.mortarHot} />
        ))}

        {/* seals */}
        {floor.ent.seals.map((s, i) => {
          const key = `${s.x},${s.y}`;
          const idx = SEAL_ORDER.indexOf(key);
          const good = dungeonState.solved.B3F || idx < dungeonState.sealProgress;
          return <SealSprite key={`s${i}`} x={s.x} y={s.y} tile={TILE}
            state={good ? "good" : "idle"} roman={SEAL_ROMAN[key]}
            revealed={allBraziersLit || dungeonState.solved.B3F} />;
        })}

        {/* braziers */}
        {floor.ent.braziers.map((b, i) => (
          <BrazierSprite key={`b${i}`} x={b.x} y={b.y} tile={TILE}
            lit={dungeonState.litBraziers.includes(`${b.x},${b.y}`)} />
        ))}

        {/* flames */}
        {floor.ent.flames.map(fl => (
          <FlamePedestal key={fl.id} flame={fl} tile={TILE}
            state={dungeonState.litFlames.includes(fl.id) || dungeonState.solved.B1F ? "lit" : "unlit"}
            onClick={() => handleFlameClick(fl)} />
        ))}

        {/* mural */}
        {floor.ent.mural && (
          <div style={{
            position: "absolute",
            left: floor.ent.mural.x * TILE + TILE * 0.1,
            top: floor.ent.mural.y * TILE + TILE * 0.1,
            width: TILE * 0.8, height: TILE * 0.8, zIndex: 5, pointerEvents: "none",
            background: `linear-gradient(135deg, ${THEME.stone.highlight}, ${THEME.stone.wall})`,
            border: `2px solid #c9a84c88`, borderRadius: 4,
            boxShadow: "0 0 10px #c9a84c33, inset 0 0 8px #000",
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 1, padding: 2,
          }}>
            {[ILKS.PHANTOM, ILKS.TIME, ILKS.DRAGON, ILKS.SPACE].map((ilk, i) => (
              <IlkSigil key={i} ilk={ilk} size={TILE * 0.17} lit={true} />
            ))}
          </div>
        )}

        {/* lever */}
        {floor.ent.lever && (
          <LeverSprite x={floor.ent.lever.x} y={floor.ent.lever.y} tile={TILE}
            pulled={!!dungeonState.solved.B2F} />
        )}

        {/* chests */}
        {floor.ent.chests.map((c, i) => {
          const t = TREASURES[`${floorId}:${c.x},${c.y}`];
          return <ChestSprite key={`c${i}`} x={c.x} y={c.y} tile={TILE}
            open={t ? dungeonState.secretsFound.has(t.id) : false} />;
        })}

        {/* Oath Tablet — appears after mural read twice */}
        {floor.ent.tablet && dungeonState.muralReads >= 2 && (
          <TabletSprite x={floor.ent.tablet.x} y={floor.ent.tablet.y} tile={TILE} />
        )}

        {/* bosses */}
        {Object.entries(BOSSES).filter(([, b]) => b.floor === floorId).map(([id, b]) => (
          <BossFigure key={id} boss={b} tile={TILE} defeated={defeated.includes(id)} />
        ))}

        {/* J'ynxi eyes — B3F only, in unlit dark, far corner. Vanish when near. */}
        {floorId === "B3F" && !allBraziersLit &&
          Math.hypot(pos.x - 21, pos.y - 15) > 5 && (
          <div style={{
            position: "absolute", left: 21 * TILE + TILE * 0.2, top: 15 * TILE + TILE * 0.3,
            zIndex: 8, pointerEvents: "none", display: "flex", gap: TILE * 0.14,
            animation: "breathe 4s ease-in-out infinite",
          }}>
            {[0, 1].map(i => <div key={i} style={{
              width: TILE * 0.12, height: TILE * 0.16, borderRadius: "50%",
              background: "#16a34a", boxShadow: "0 0 6px #16a34a",
            }} />)}
          </div>
        )}

        {/* Sanctum boss aura */}
        {floorId === "SANCTUM" && (
          <div style={{
            position: "absolute", left: 0, top: 0,
            width: floor.cols * TILE, height: 4 * TILE,
            background: `radial-gradient(ellipse at 50% 0%, ${THEME.bossAura}, transparent 70%)`,
            zIndex: 2, pointerEvents: "none",
            animation: "breathe 3s ease-in-out infinite",
          }} />
        )}

        {/* ambient tint */}
        {meta.tint && (
          <div style={{
            position: "absolute", inset: 0, background: meta.tint,
            zIndex: 2, pointerEvents: "none",
          }} />
        )}

        {/* ── TARGET BRACKETS — gold corners marking exactly what
            Q will interact with. One language for "this responds". ── */}
        {nearby && nearby.x !== undefined && !closeup && !tabletOpen && (
          <div style={{
            position: "absolute",
            left: nearby.x * TILE - 3, top: nearby.y * TILE - 3,
            width: TILE + 6, height: TILE + 6,
            zIndex: 9, pointerEvents: "none",
            animation: "targetPulse 1.2s ease-in-out infinite",
          }}>
            {[
              { left: 0, top: 0, borderWidth: "2px 0 0 2px" },
              { right: 0, top: 0, borderWidth: "2px 2px 0 0" },
              { left: 0, bottom: 0, borderWidth: "0 0 2px 2px" },
              { right: 0, bottom: 0, borderWidth: "0 2px 2px 0" },
            ].map((s, i) => (
              <div key={i} style={{
                position: "absolute", width: "26%", height: "26%",
                borderStyle: "solid", borderColor: nearby.accentColor || "#c9a84c",
                ...s,
              }} />
            ))}
          </div>
        )}

        {/* Player */}
        <PlayerEntity
          pos={pos} facing={facing} gender={gender}
          walkFrame={walkFrame} tile={TILE}
          opacity={1}
          motion={motion} idleStep={idleStep}
          companionId={companion.companionId}
          radiantTier={companion.radiantTier}
          showCompanion={true}
        />
      </div>

      {/* ── VIGNETTE — heavy dark-fantasy framing ── */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 40, pointerEvents: "none",
        background: "radial-gradient(ellipse at 50% 45%, transparent 40%, rgba(0,0,0,0.55) 80%, rgba(0,0,0,0.9) 100%)",
      }} />
      {/* subtle cold top-light */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 40, pointerEvents: "none",
        background: `linear-gradient(180deg, ${THEME.mortarHot}0a, transparent 30%)`,
      }} />

      {/* ── VOID DUST — drifting motes ── */}
      {!meta.dark && (
        <div style={{ position: "fixed", inset: 0, zIndex: 41, pointerEvents: "none", overflow: "hidden" }}>
          {Array.from({ length: 14 }).map((_, i) => (
            <div key={i} style={{
              position: "absolute",
              left: `${(i * 137) % 100}%`, top: `${(i * 71 + 30) % 100}%`,
              width: i % 3 === 0 ? 3 : 2, height: i % 3 === 0 ? 3 : 2,
              borderRadius: "50%",
              background: i % 4 === 0 ? "#c9a84c" : "#b9a8ff",
              boxShadow: `0 0 4px ${i % 4 === 0 ? "#c9a84c" : "#b9a8ff"}`,
              animation: `dustDrift ${9 + (i % 5) * 2.4}s linear ${i * 0.9}s infinite`,
            }} />
          ))}
        </div>
      )}

      {/* Mobile D-pad */}
      {mobile && <MobileDpad keysRef={keysRef} />}

      {/* Mobile interact button */}
      {mobile && nearby && !closeup && (
        <button aria-label="Interact" onClick={tryInteract} style={{
          position: "fixed", right: 18, bottom: 110, zIndex: 70,
          width: 58, height: 58, borderRadius: "50%",
          background: `${nearby.accentColor}33`, color: nearby.accentColor,
          border: `2px solid ${nearby.accentColor}`, fontSize: 20,
          fontFamily: SD.fontDisplay, cursor: "pointer",
        }}>◆</button>
      )}

      {/* Toast */}
      {dialogue && (
        <DialogueToast
          text={dialogue.text} color={dialogue.color}
          onDismiss={() => setDialogue(null)}
        />
      )}

      {/* Proximity bubble */}
      {nearby && !closeup && !tabletOpen && (
        <ProximityBubble
          verb={nearby.verb} label={nearby.label}
          color={nearby.accentColor} portrait={true}
          onActivate={() => openInteractable(nearby)}
          onDismiss={() => setNearby(null)}
        />
      )}

      {/* Visual novel closeup */}
      {closeup && (
        <ObjectCloseup
          title={closeup.title} accent={closeup.accent}
          lines={closeup.lines} options={closeup.options}
          onAction={handleCloseupAction}
          onClose={() => { setCloseup(null); lockRef.current = false; }}
        />
      )}

      {/* Oath Tablet puzzle */}
      {tabletOpen && (
        <PuzzleTablet
          onCorrect={handleTabletCorrect}
          onWrong={handleTabletWrong}
          onClose={() => { setTabletOpen(false); lockRef.current = false; }}
        />
      )}

      {/* Floor title card */}
      {titleCard && (
        <FloorTitleCard meta={titleCard} onDone={() => setTitleCard(null)} />
      )}

      {/* Wormhole flash */}
      {wormhole && <WormholeFlash onComplete={() => setWormhole(false)} />}

      {/* Controls hint */}
      {!mobile && (
        <div style={{
          position: "fixed", bottom: 12, left: "50%", transform: "translateX(-50%)",
          fontSize: 9, color: SD.starDim, letterSpacing: 2, zIndex: 50,
          background: "rgba(0,0,0,0.5)", padding: "4px 12px", borderRadius: 10,
        }}>
          WASD · Q TO INTERACT · CLICK FLAMES · Q ON SUSPICIOUS TILES
        </div>
      )}
    </div>
  );
}
